import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Search,
  Filter,
  Plus,
  MoreVertical,
  Mail,
  Phone,
  Users,
  DollarSign,
  Eye,
  Trash2,
  Edit,
  TrendingUp,
  RefreshCw,
  Percent,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Building2,
  Shield,
  UserCog,
  Briefcase,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { motion } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import AddRepresentativeDialog from '../../components/admin/AddRepresentativeDialog';
import EditRepresentativeDialog from '../../components/admin/EditRepresentativeDialog';
import DeleteRepresentativeDialog from '../../components/admin/DeleteRepresentativeDialog';

const REP_TYPE_CONFIG = {
  rep1: {
    label: 'Internal',
    color: 'bg-blue-100 text-blue-700',
    gradient: 'from-blue-500 to-indigo-600',
    icon: Shield,
  },
  rep2: {
    label: 'Senior/Regional',
    color: 'bg-purple-100 text-purple-700',
    gradient: 'from-purple-500 to-violet-600',
    icon: UserCog,
  },
  rep3: {
    label: 'Partner',
    color: 'bg-amber-100 text-amber-700',
    gradient: 'from-amber-500 to-orange-600',
    icon: Building2,
  },
};

const EXPERIENCE_LABELS = {
  junior: 'Junior',
  mid: 'Mid-Level',
  senior: 'Senior',
  lead: 'Lead',
  director: 'Director',
};

export default function ManageRepresentatives() {
  const navigate = useNavigate();
  const [representatives, setRepresentatives] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    typeCounts: { rep1: 0, rep2: 0, rep3: 0 },
    totalStudentsManaged: 0,
    totalCommissionsPaid: 0,
    pendingCommissions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 1,
  });

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRep, setSelectedRep] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getRepresentativeStats();
      setStats(response.stats || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchRepresentatives = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getRepresentatives({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        status: statusFilter,
        repType: typeFilter,
      });
      setRepresentatives(response.representatives || []);
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages,
        }));
      }
    } catch (error) {
      toast.error('Failed to fetch representatives');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchRepresentatives();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchRepresentatives]);

  const handleStatusToggle = async (rep, newStatus) => {
    try {
      const repId = rep.userId || rep._id;
      await adminAPI.updateRepresentativeStatus(repId, newStatus);
      toast.success(`Representative ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchRepresentatives();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update representative status');
    }
  };

  const handleEdit = (rep) => {
    setSelectedRep(rep);
    setEditDialogOpen(true);
  };

  const handleDelete = (rep) => {
    setSelectedRep(rep);
    setDeleteDialogOpen(true);
  };

  const handleView = (rep) => {
    const repId = rep.userId || rep._id;
    navigate(`/admin/representatives/${repId}`);
  };

  const handleRefresh = () => {
    fetchRepresentatives();
    fetchStats();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getRepConfig = (role) => REP_TYPE_CONFIG[role] || REP_TYPE_CONFIG.rep1;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="manage-representatives-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">
              Representative Management
            </h1>
            <p className="text-gray-600">
              Manage all representatives, their commissions, and performance
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setAddDialogOpen(true)} data-testid="add-rep-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Representative
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
                <p className="text-sm text-gray-600">Total Representatives</p>
              </div>
            </div>
            {/* Type breakdown */}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-stone-100">
              <span className="text-xs text-blue-600 font-medium">
                R1: {stats.typeCounts?.rep1 || 0}
              </span>
              <span className="text-xs text-purple-600 font-medium">
                R2: {stats.typeCounts?.rep2 || 0}
              </span>
              <span className="text-xs text-amber-600 font-medium">
                R3: {stats.typeCounts?.rep3 || 0}
              </span>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active || 0}</p>
                <p className="text-sm text-gray-600">Active Representatives</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  ${(stats.totalCommissionsPaid || 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">Total Commissions Paid</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalStudentsManaged || 0}
                </p>
                <p className="text-sm text-gray-600">Students Managed</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card className="p-4 bg-white border-stone-200/80">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search representatives by name or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="pl-10"
                data-testid="search-reps-input"
              />
            </div>
            <Select
              value={typeFilter}
              onValueChange={(value) => {
                setTypeFilter(value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="w-[200px]" data-testid="filter-type-select">
                <Briefcase className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="rep1">Internal (Rep1)</SelectItem>
                <SelectItem value="rep2">Senior/Regional (Rep2)</SelectItem>
                <SelectItem value="rep3">Partner (Rep3)</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-status-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Representatives Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : representatives.length === 0 ? (
          <Card className="flex flex-col items-center justify-center h-64 text-gray-500 bg-white border-stone-200/80">
            <Users className="w-12 h-12 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No representatives found</p>
            <p className="text-sm">Try adjusting your filters or add a new representative</p>
            <Button className="mt-4" onClick={() => setAddDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Representative
            </Button>
          </Card>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="reps-grid"
          >
            {representatives.map((rep, index) => {
              const config = getRepConfig(rep.role);
              const TypeIcon = config.icon;
              return (
                <motion.div
                  key={rep.userId || rep._id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="p-6 bg-white border-stone-200/80 hover:shadow-lg hover:shadow-stone-200/50 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-12 h-12 bg-gradient-to-br ${config.gradient} rounded-full flex items-center justify-center text-white font-semibold text-lg`}
                        >
                          {rep.firstName?.[0]}
                          {rep.lastName?.[0]}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {rep.firstName} {rep.lastName}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={config.color}>
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {config.label}
                            </Badge>
                            <Badge
                              className={
                                rep.isActive !== false
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-stone-100 text-gray-600'
                              }
                            >
                              {rep.isActive !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(rep)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(rep)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDelete(rep)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="space-y-2.5 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{rep.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{rep.phone || 'Not provided'}</span>
                      </div>
                      {(rep.assignedRegion || rep.country) && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span>{rep.assignedRegion || rep.country}</span>
                        </div>
                      )}
                      {rep.organization && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Building2 className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{rep.organization}</span>
                        </div>
                      )}
                    </div>

                    {/* Metrics Row */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-lg text-center">
                        <p className="text-lg font-bold text-blue-700">
                          {rep.totalStudents || 0}
                        </p>
                        <p className="text-[11px] text-blue-600">Students</p>
                      </div>
                      <div className="p-2.5 bg-green-50 border border-green-100 rounded-lg text-center">
                        <p className="text-lg font-bold text-green-700">
                          ${(rep.totalEarnings || 0).toLocaleString()}
                        </p>
                        <p className="text-[11px] text-green-600">Earned</p>
                      </div>
                    </div>

                    {/* Commission Rate */}
                    <div className="p-3 bg-stone-50 border border-stone-100 rounded-lg mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Percent className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">Commission Rate</span>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {rep.commissionRate || rep.commissionPercentage || 10}%
                        </span>
                      </div>
                      {(rep.pendingEarnings || 0) > 0 && (
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-200">
                          <span className="text-sm text-amber-600">Pending</span>
                          <span className="font-medium text-amber-600">
                            ${(rep.pendingEarnings || 0).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Status Toggle */}
                    <div className="flex items-center justify-between p-3 bg-stone-50 border border-stone-100 rounded-lg mb-4">
                      <span className="text-sm text-gray-600">Account Status</span>
                      <Switch
                        checked={rep.isActive !== false}
                        onCheckedChange={(checked) => handleStatusToggle(rep, checked)}
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-4 border-t border-stone-100">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleView(rep)}
                        data-testid={`view-rep-${index}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleEdit(rep)}
                        data-testid={`edit-rep-${index}`}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(rep)}
                        data-testid={`delete-rep-${index}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && representatives.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} representatives
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-gray-600 px-3">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddRepresentativeDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={() => {
          fetchRepresentatives();
          fetchStats();
        }}
      />

      <EditRepresentativeDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        representative={selectedRep}
        onSuccess={() => {
          fetchRepresentatives();
          fetchStats();
        }}
      />

      <DeleteRepresentativeDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        representative={selectedRep}
        onSuccess={() => {
          fetchRepresentatives();
          fetchStats();
        }}
      />
    </AdminLayout>
  );
}
